/**
 * CNSS / CNOPS — Paramètres des organismes d'assurance maladie
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Building2, Settings, FileText, Shield, Plus, Save, TestTube2 } from "lucide-react";

export const Route = createFileRoute("/referential/cnss")({
  component: CNSSCNOPSPage,
});

interface Organisme {
  code: string;
  name: string;
  fullName: string;
  color: string;
  contactEmail: string;
  submissionProtocol: "FSE_XML" | "FSE_CSV" | "API_REST" | "EDI";
  defaultCoverage: number;
  daysToPayment: number;
  requiresPreApproval: boolean;
  fexEndpoint: string;
}

const ORGANISMES: Organisme[] = [
  {
    code: "CNSS",
    name: "CNSS",
    fullName: "Caisse Nationale de Sécurité Sociale",
    color: "blue",
    contactEmail: "fse@cnss.ma",
    submissionProtocol: "FSE_XML",
    defaultCoverage: 80,
    daysToPayment: 30,
    requiresPreApproval: false,
    fexEndpoint: "https://fse.cnss.ma/api/v2/fse",
  },
  {
    code: "CNOPS",
    name: "CNOPS",
    fullName: "Caisse Nationale des Organismes de Prévoyance Sociale",
    color: "green",
    contactEmail: "edi@cnops.ma",
    submissionProtocol: "FSE_XML",
    defaultCoverage: 90,
    daysToPayment: 21,
    requiresPreApproval: true,
    fexEndpoint: "https://fse.cnops.ma/api/v2/fse",
  },
  {
    code: "AMO",
    name: "AMO",
    fullName: "Assurance Maladie Obligatoire (régime étudiants)",
    color: "purple",
    contactEmail: "amo@enssup.gov.ma",
    submissionProtocol: "API_REST",
    defaultCoverage: 80,
    daysToPayment: 30,
    requiresPreApproval: false,
    fexEndpoint: "https://api.amo.gov.ma/v1/fse",
  },
  {
    code: "PRIVATE",
    name: "Privé",
    fullName: "Assurances privées (AXA, Wafa Assurance, Saham, Atlanta...)",
    color: "amber",
    contactEmail: "varies",
    submissionProtocol: "FSE_CSV",
    defaultCoverage: 100,
    daysToPayment: 45,
    requiresPreApproval: true,
    fexEndpoint: "varies",
  },
];

const COVERAGE_RULES = [
  { code: "MAL-STD", label: "Maladies standard", cnss: 80, cnops: 90, amo: 80, private: 100 },
  { code: "MAT-ACC", label: "Maternité (accouchement)", cnss: 100, cnops: 100, amo: 100, private: 100 },
  { code: "ALD-30", label: "ALD 30 (liste)", cnss: 100, cnops: 100, amo: 100, private: 100 },
  { code: "CHIR-AMB", label: "Chirurgie ambulatoire", cnss: 80, cnops: 90, amo: 80, private: 90 },
  { code: "HOSP-STD", label: "Hospitalisation standard", cnss: 80, cnops: 90, amo: 80, private: 100 },
  { code: "REA-ICU", label: "Réanimation / Soins intensifs", cnss: 100, cnops: 100, amo: 100, private: 100 },
  { code: "DIALYSE", label: "Dialyse", cnss: 100, cnops: 100, amo: 100, private: 100 },
  { code: "DENTAIRE", label: "Dentaire", cnss: 0, cnops: 70, amo: 0, private: 80 },
  { code: "OPTIQUE", label: "Optique", cnss: 0, cnops: 60, amo: 0, private: 70 },
];

function CNSSCNOPSPage() {
  const [selected, setSelected] = useState<Organisme>(ORGANISMES[0]);
  const [editMode, setEditMode] = useState(false);

  return (
    <div className="space-y-4 p-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <Building2 className="h-5 w-5" /> CNSS / CNOPS / AMO / Privé
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Paramètres des organismes d'assurance maladie · 4 organismes · Protocoles de soumission FSE
        </p>
      </div>

      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        {ORGANISMES.map((o) => (
          <Card
            key={o.code}
            className={`cursor-pointer transition ${selected.code === o.code ? "border-primary" : "hover:border-primary/30"}`}
            onClick={() => setSelected(o)}
          >
            <CardContent className="p-4 text-center">
              <div className={`text-2xl font-bold text-${o.color}-400`}>{o.code}</div>
              <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{o.fullName}</div>
              <div className="mt-2 text-[10px] text-muted-foreground">{o.submissionProtocol}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="settings">
        <TabsList>
          <TabsTrigger value="settings"><Settings className="h-3.5 w-3.5 mr-1" /> Paramètres</TabsTrigger>
          <TabsTrigger value="coverage"><Shield className="h-3.5 w-3.5 mr-1" /> Couverture</TabsTrigger>
          <TabsTrigger value="protocol"><FileText className="h-3.5 w-3.5 mr-1" /> Protocole FSE</TabsTrigger>
          <TabsTrigger value="test"><TestTube2 className="h-3.5 w-3.5 mr-1" /> Test connexion</TabsTrigger>
        </TabsList>

        <TabsContent value="settings" className="space-y-3">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {selected.code} — {selected.fullName}
                {editMode ? <Badge variant="outline">Édition</Badge> : <Badge variant="secondary">Lecture seule</Badge>}
              </CardTitle>
              <CardDescription>Paramètres généraux de l'organisme</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Code" value={selected.code} />
                <Field label="Nom complet" value={selected.fullName} />
                <Field label="Email contact" value={selected.contactEmail} />
                <Field label="Endpoint FSE" value={selected.fexEndpoint} />
                <Field label="Protocole" value={selected.submissionProtocol} />
                <Field label="Couverture par défaut" value={`${selected.defaultCoverage}%`} />
                <Field label="Délai paiement" value={`${selected.daysToPayment} jours`} />
                <Field label="Pré-approbation requise" value={selected.requiresPreApproval ? "Oui" : "Non"} />
              </div>
              <div className="flex items-center gap-2 pt-2">
                <Button onClick={() => setEditMode(!editMode)} variant={editMode ? "default" : "outline"}>
                  {editMode ? <><Save className="h-3.5 w-3.5 mr-1" /> Enregistrer</> : "Modifier"}
                </Button>
                <Button variant="outline"><TestTube2 className="h-3.5 w-3.5 mr-1" /> Tester la connexion</Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="coverage" className="space-y-2">
          <Card>
            <CardHeader>
              <CardTitle>Règles de couverture par type d'acte</CardTitle>
              <CardDescription>Pourcentage pris en charge par chaque organisme (ticket modérateur inverse)</CardDescription>
            </CardHeader>
            <CardContent>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-2 text-muted-foreground text-xs">Type</th>
                    {ORGANISMES.map((o) => (
                      <th key={o.code} className="text-center py-2 text-xs text-muted-foreground">{o.code}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {COVERAGE_RULES.map((r) => (
                    <tr key={r.code} className="border-b border-white/5">
                      <td className="py-1.5">
                        <code className="text-[10px] font-mono text-muted-foreground mr-2">{r.code}</code>
                        {r.label}
                      </td>
                      <td className="text-center py-1.5">
                        <CoverageBadge value={r.cnss} />
                      </td>
                      <td className="text-center py-1.5">
                        <CoverageBadge value={r.cnops} />
                      </td>
                      <td className="text-center py-1.5">
                        <CoverageBadge value={r.amo} />
                      </td>
                      <td className="text-center py-1.5">
                        <CoverageBadge value={r.private} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="protocol" className="space-y-2">
          <Card>
            <CardHeader>
              <CardTitle>Protocole de soumission FSE — {selected.code}</CardTitle>
              <CardDescription>Format et endpoint utilisés pour la soumission</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div>
                <div className="text-xs text-muted-foreground">Format</div>
                <div className="font-mono text-xs bg-white/5 p-2 rounded mt-1">
                  {selected.submissionProtocol === "FSE_XML" && "FSE au format XML (norme CNAM Maroc v2.4)"}
                  {selected.submissionProtocol === "FSE_CSV" && "FSE au format CSV (assurances privées)"}
                  {selected.submissionProtocol === "API_REST" && "API REST (OpenAPI 3.0)"}
                  {selected.submissionProtocol === "EDI" && "EDI X12 (rare)"}
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Endpoint</div>
                <div className="font-mono text-xs bg-white/5 p-2 rounded mt-1">{selected.fexEndpoint}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Authentification</div>
                <div className="font-mono text-xs bg-white/5 p-2 rounded mt-1">TLS 1.3 + mTLS (certificat AMO/CNSS)</div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="test" className="space-y-2">
          <Card>
            <CardHeader>
              <CardTitle>Test de connexion — {selected.code}</CardTitle>
              <CardDescription>Vérifie que votre DIKDOC peut parler à l'organisme</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button>
                <TestTube2 className="h-3.5 w-3.5 mr-1" /> Lancer un ping
              </Button>
              <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 text-sm">
                ✓ TLS handshake OK
                <br />✓ Certificat valide (expire 2027-04-15)
                <br />✓ Authentification acceptée
                <br />✓ Réponse API: 200 OK en 142ms
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</div>
      <div className="text-sm font-medium">{value}</div>
    </div>
  );
}

function CoverageBadge({ value }: { value: number }) {
  return (
    <Badge variant={value === 100 ? "secondary" : value === 0 ? "destructive" : "outline"} className="font-mono text-[11px]">
      {value}%
    </Badge>
  );
}
